import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, InjectionToken, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

export type IdentityDocumentApiType = 'INE' | 'PASSPORT';

export type VerifyIdentityProcessType = 'IDENTITY_VALIDATION' | 'IDENTITY_AND_SIGNATURE';
export type VerifyIdentityStepKey = 'UPLOAD_DOCUMENT' | 'SELFIE' | 'SIGNATURE' | 'CONFIRM';

export type DigitalSignatureProcessType = 'DIGITAL_SIGNATURE';
export type DigitalSignatureStepKey =
  | 'REVIEW_DOCUMENT'
  | 'UPLOAD_DOCUMENT'
  | 'SIGNATURE'
  | 'CONFIRM';

export interface VerificationMultimediaDto {
  uuid: string;
  name?: string;
  contentType?: string;
  order?: number;
  size?: string;
  url: string;
}

export interface DigitalSignatureSignerDto {
  name: string;
  type: string;
  signed?: boolean;
}

export interface DigitalSignaturePlacementDto {
  uuid: string;
  signer: DigitalSignatureSignerDto;
  pageNumber: number;
  xPercent: number;
  yPercent: number;
  widthPercent: number;
  heightPercent: number;
}

export interface VerifyIdentityStepDto {
  processType: VerifyIdentityProcessType;
  currentStep: number;
  totalSteps: number;
  stepKey: VerifyIdentityStepKey;
  signerName?: string;
  typeName?: string;
  requestedAt?: string;
  documentType?: IdentityDocumentApiType;
  documentFront?: VerificationMultimediaDto;
  documentBack?: VerificationMultimediaDto;
  faceSimilarity?: number;
  faceExtraction?: VerificationMultimediaDto;
  selfie?: VerificationMultimediaDto;
  signature?: VerificationMultimediaDto;
}

export interface DigitalSignatureStepDto {
  processType: DigitalSignatureProcessType;
  currentStep: number;
  totalSteps: number;
  stepKey: DigitalSignatureStepKey;
  documentType?: IdentityDocumentApiType;
  signerName?: string;
  documentName?: string;
  requestedAt?: string;
  originalDocument?: VerificationMultimediaDto;
  placements?: DigitalSignaturePlacementDto[];
  signers?: DigitalSignatureSignerDto[];
  documentFront?: VerificationMultimediaDto;
  documentBack?: VerificationMultimediaDto;
  faceSimilarity?: number;
  faceExtraction?: VerificationMultimediaDto;
  selfie?: VerificationMultimediaDto;
  signature?: VerificationMultimediaDto;
}

interface ApiResponse<T> {
  message: string;
  status: number;
  data: T;
}

interface GuestTokenDto {
  tokenGuest: string;
}

export const VERIFICATION_API_URL = new InjectionToken<string>('VERIFICATION_API_URL');
export const VERIFICATION_AUTH_API_URL = new InjectionToken<string>('VERIFICATION_AUTH_API_URL');
export const VERIFICATION_AUTH_GUEST_SECRET = new InjectionToken<string>(
  'VERIFICATION_AUTH_GUEST_SECRET',
);

@Injectable({ providedIn: 'root' })
export class VerificationGuestTokenService {
  private readonly http = inject(HttpClient);
  private readonly authApiUrl = normalizeApiUrl(inject(VERIFICATION_AUTH_API_URL));
  private readonly jwtSecret = inject(VERIFICATION_AUTH_GUEST_SECRET);
  private guestToken: string | null = null;

  async getToken(): Promise<string> {
    if (this.guestToken) {
      return this.guestToken;
    }

    const response = await firstValueFrom(
      this.http.post<ApiResponse<GuestTokenDto>>(
        `${this.authApiUrl}/guest/guest-token`,
        null,
        { params: { jwtSecret: this.jwtSecret } },
      ),
    );
    this.guestToken = response.data.tokenGuest;
    return this.guestToken;
  }

  clearToken(): void {
    this.guestToken = null;
  }
}

@Injectable({ providedIn: 'root' })
export class VerifyIdentityApiService {
  private readonly http = inject(HttpClient);
  private readonly guestToken = inject(VerificationGuestTokenService);
  private readonly baseUrl = `${normalizeApiUrl(inject(VERIFICATION_API_URL))}/verify-identity`;

  async getStep(token: string, reset = false): Promise<VerifyIdentityStepDto> {
    const headers = await this.authHeaders();
    const response = await firstValueFrom(
      this.http.get<ApiResponse<VerifyIdentityStepDto>>(`${this.baseUrl}/step`, {
        headers,
        params: { token, reset },
      }),
    );
    return response.data;
  }

  async uploadDocument(
    sessionToken: string,
    documentType: IdentityDocumentApiType,
    front: File,
    back?: File | null,
  ): Promise<void> {
    const formData = new FormData();
    formData.append('sessionToken', sessionToken);
    formData.append('documentType', documentType);
    formData.append('front', front);
    if (back) {
      formData.append('back', back);
    }

    await this.postForm(`${this.baseUrl}/document`, formData);
  }

  async uploadSelfie(sessionToken: string, selfie: File): Promise<void> {
    const formData = new FormData();
    formData.append('sessionToken', sessionToken);
    formData.append('selfie', selfie);

    await this.postForm(`${this.baseUrl}/selfie`, formData);
  }

  async uploadSignature(sessionToken: string, signature: File): Promise<void> {
    const formData = new FormData();
    formData.append('sessionToken', sessionToken);
    formData.append('signature', signature);

    await this.postForm(`${this.baseUrl}/signature`, formData);
  }

  private async postForm(url: string, formData: FormData): Promise<void> {
    const headers = await this.authHeaders();
    await firstValueFrom(this.http.post<ApiResponse<string>>(url, formData, { headers }));
  }

  private async authHeaders(): Promise<HttpHeaders> {
    const token = await this.guestToken.getToken();
    return new HttpHeaders({ Authorization: `Bearer ${token}` });
  }
}

@Injectable({ providedIn: 'root' })
export class DigitalSignatureApiService {
  private readonly http = inject(HttpClient);
  private readonly guestToken = inject(VerificationGuestTokenService);
  private readonly baseUrl = `${normalizeApiUrl(inject(VERIFICATION_API_URL))}/digital-signature`;

  async getStep(token: string, reset = false): Promise<DigitalSignatureStepDto> {
    const headers = await this.authHeaders();
    const response = await firstValueFrom(
      this.http.get<ApiResponse<DigitalSignatureStepDto>>(`${this.baseUrl}/step`, {
        headers,
        params: { token, reset },
      }),
    );
    return response.data;
  }

  async uploadDocument(
    sessionToken: string,
    documentType: IdentityDocumentApiType,
    front: File,
    back?: File | null,
  ): Promise<void> {
    const formData = new FormData();
    formData.append('sessionToken', sessionToken);
    formData.append('documentType', documentType);
    formData.append('front', front);
    if (back) {
      formData.append('back', back);
    }

    await this.postForm(`${this.baseUrl}/document`, formData);
  }

  async uploadSelfie(sessionToken: string, selfie: File): Promise<void> {
    const formData = new FormData();
    formData.append('sessionToken', sessionToken);
    formData.append('selfie', selfie);

    await this.postForm(`${this.baseUrl}/selfie`, formData);
  }

  async uploadSignature(sessionToken: string, signature: File): Promise<void> {
    const formData = new FormData();
    formData.append('sessionToken', sessionToken);
    formData.append('signature', signature);

    await this.postForm(`${this.baseUrl}/signature`, formData);
  }

  private async postForm(url: string, formData: FormData): Promise<void> {
    const headers = await this.authHeaders();
    await firstValueFrom(this.http.post<ApiResponse<string>>(url, formData, { headers }));
  }

  private async authHeaders(): Promise<HttpHeaders> {
    const token = await this.guestToken.getToken();
    return new HttpHeaders({ Authorization: `Bearer ${token}` });
  }
}

export function apiErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    const apiError = error.error as { error?: unknown };
    const detail = apiError.error;
    if (typeof detail === 'string') {
      return detail;
    }
    if (detail && typeof detail === 'object') {
      const firstField = Object.values(detail as Record<string, unknown>)[0];
      if (Array.isArray(firstField) && typeof firstField[0] === 'string') {
        return firstField[0];
      }
    }
  }

  return 'No pudimos completar la solicitud. Intenta de nuevo.';
}

export function apiErrorStatus(error: unknown): number | null {
  if (!error || typeof error !== 'object' || !('status' in error)) return null;

  const status = (error as { status?: unknown }).status;
  return typeof status === 'number' ? status : null;
}

export function dataUrlToFile(dataUrl: string, filename: string): File {
  const [metadata, base64Data] = dataUrl.split(',');
  const mimeType = metadata.match(/data:(.*?);base64/)?.[1] ?? 'image/png';
  const binary = atob(base64Data);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return new File([bytes], filename, { type: mimeType });
}

function isApiError(error: unknown): error is { error: unknown } {
  return Boolean(error && typeof error === 'object' && 'error' in error);
}

function normalizeApiUrl(url: string): string {
  return url.replace(/\/+$/, '');
}
